import { Service } from "../flatbuffers/generated/service/service";
import { DeviceServiceContent } from "../flatbuffers/generated/service/device-service-content";

/**
 * How a service mode should be presented: its display name and styling.
 */
export interface ServiceModeDisplay {
  displayName: string;
  abbreviation: string;
  color: string;
}

interface ServiceLabel {
  displayName: string;
  abbreviation: string;
}

// ---------------------------------------------------------------------------
// Ant Design Tag preset colors, excluding red/green and their variants
// to avoid implying good/bad semantics.
// ---------------------------------------------------------------------------
const COLOR_PALETTE: readonly string[] = [
  "blue",
  "purple",
  "cyan",
  "magenta",
  "orange",
  "volcano",
  "geekblue",
  "gold",
  "lime",
];

/**
 * Deterministic color assignment for a service mode.
 * Uses a simple hash of the content-type + code so the same service mode
 * always maps to the same palette color, without hardcoding any particular
 * service to any particular color.
 */
export function serviceColor(contentType: DeviceServiceContent, code: Service | Service): string {
  // Mix content type into the hash so elevator code 0 and escalator code 0
  // don't necessarily land on the same color.
  const hash = contentType * 257 + code;
  return COLOR_PALETTE[
    ((hash % COLOR_PALETTE.length) + COLOR_PALETTE.length) % COLOR_PALETTE.length
  ];
}

/**
 * Resolve a service color from the enum key name stored in serviceMap
 * (e.g. "FirePhase1", "Inspection"). Tries elevator first, then escalator.
 * Produces the same color as serviceColor() / getServiceModeDisplay() for
 * the same service mode, so both monitoring views stay in sync.
 */
export function serviceColorByName(enumKey: string): string {
  const code = (Service as Record<string, unknown>)[enumKey];
  if (typeof code === "number") {
    return serviceColor(DeviceServiceContent.ElevatorServiceMessage, code);
  }
  return "default";
}

// ---------------------------------------------------------------------------
// Human-readable labels — names and abbreviations only, no color.
// ---------------------------------------------------------------------------
const SERVICE_LABELS: Partial<Record<Service, ServiceLabel>> = {
  [Service.Auto]: { displayName: "Automatic", abbreviation: "AUTO" },
  [Service.Norm]: { displayName: "Normal", abbreviation: "NORM" },
  [Service.Run]: { displayName: "Run", abbreviation: "RUN" },
  [Service.Oos]: {
    displayName: "Out of Service",
    abbreviation: "OOS",
  },
  [Service.Ins]: { displayName: "Inspection", abbreviation: "INS" },
  [Service.InsCons]: {
    displayName: "Inspection Console",
    abbreviation: "INSC",
  },
  [Service.CtIns]: {
    displayName: "CT Inspection",
    abbreviation: "CINS",
  },
  [Service.CpIns]: {
    displayName: "CP Inspection",
    abbreviation: "CPIS",
  },
  [Service.HaIns]: {
    displayName: "HA Inspection",
    abbreviation: "HINS",
  },
  [Service.MrIns]: {
    displayName: "MR Inspection",
    abbreviation: "MINS",
  },
  [Service.MOS]: { displayName: "MOS", abbreviation: "MOS" },
  [Service.Ind]: { displayName: "Independent", abbreviation: "IND" },
  [Service.Test]: { displayName: "Test", abbreviation: "TEST" },
  [Service.FireMain]: {
    displayName: "Fire Phase 1",
    abbreviation: "FIRE",
  },
  [Service.FireAlt]: {
    displayName: "Fire Alternate",
    abbreviation: "FALT",
  },
  [Service.FirePhase1]: {
    displayName: "Fire Phase 1",
    abbreviation: "FPH1",
  },
  [Service.FirePhase2]: {
    displayName: "Fire Phase 2",
    abbreviation: "FPH2",
  },
  [Service.HospitalPhase1]: {
    displayName: "Hospital Phase 1",
    abbreviation: "HPH1",
  },
  [Service.HospitalPhase2]: {
    displayName: "Hospital Phase 2",
    abbreviation: "HPH2",
  },
  [Service.Shuttle]: { displayName: "Shuttle", abbreviation: "SHUT" },
  [Service.Sabbath]: { displayName: "Sabbath", abbreviation: "SABB" },
  [Service.Construction]: {
    displayName: "Construction",
    abbreviation: "CONS",
  },
  [Service.Earthquake]: {
    displayName: "Earthquake",
    abbreviation: "EQKE",
  },
  [Service.Attendant]: {
    displayName: "Attendant",
    abbreviation: "ATND",
  },
  [Service.CodeBlue]: {
    displayName: "Code Blue",
    abbreviation: "CBLU",
  },
  [Service.Homing]: { displayName: "Homing", abbreviation: "HOME" },
  [Service.Stalled]: { displayName: "Stalled", abbreviation: "STLD" },
  [Service.CarToLobby]: {
    displayName: "Car to Lobby",
    abbreviation: "CLBY",
  },
  [Service.LoadBypass]: {
    displayName: "Load Bypass",
    abbreviation: "LBYP",
  },
  [Service.MedicalEmergency]: {
    displayName: "Medical Emergency",
    abbreviation: "MEDC",
  },
  [Service.VIP]: { displayName: "VIP", abbreviation: "VIP" },
  [Service.SecurityRecall]: {
    displayName: "Security Recall",
    abbreviation: "SREC",
  },
  [Service.EmergencyPower]: {
    displayName: "Emergency Power",
    abbreviation: "EPWR",
  },
  [Service.Parked]: { displayName: "Parked", abbreviation: "PARK" },
  [Service.Idle]: { displayName: "Idle", abbreviation: "IDLE" },
  [Service.Initializing]: {
    displayName: "Initializing",
    abbreviation: "INIT",
  },
  [Service.EmergencyStopButton]: {
    displayName: "Emergency Stop",
    abbreviation: "ESTP",
  },
  [Service.CarEmergency]: {
    displayName: "Car Emergency",
    abbreviation: "CEMG",
  },
  [Service.EmergencyRecall]: {
    displayName: "Emergency Recall",
    abbreviation: "EREC",
  },
  [Service.Night]: { displayName: "Night", abbreviation: "NITE" },
  [Service.Unknown]: { displayName: "Unknown", abbreviation: "UNK" },
};

/**
 * Split a PascalCase enum name into separate words.
 * "FrontDoorNudging" → "Front Door Nudging"
 * Handles consecutive capitals like "ARD" or "EPC" as single tokens.
 */
function splitPascalCase(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}

/**
 * Build a short abbreviation from a PascalCase name.
 * Takes the first letter of each word, uppercase, max 4 chars.
 * "FrontDoorNudging" → "FDN", "Security" → "SEC" (first 3 of single word)
 */
function abbreviate(name: string): string {
  const words = splitPascalCase(name).split(" ");
  if (words.length === 1) {
    return words[0].substring(0, 4).toUpperCase();
  }
  return words
    .map((w) => w[0])
    .join("")
    .substring(0, 4)
    .toUpperCase();
}

function fallbackLabel(code: Service | Service): ServiceLabel {
  const enumName = Service[code as Service] ?? Service[code as Service];
  if (enumName && typeof enumName === "string") {
    return {
      displayName: splitPascalCase(enumName),
      abbreviation: abbreviate(enumName),
    };
  }
  return {
    displayName: `Service ${code}`,
    abbreviation: `S${code}`,
  };
}

/**
 * Maps a service-mode value to its display name and styling.
 *
 * Returns a neutral default for null, so a lift with no reported mode renders
 * blank rather than as an error.
 */
export function getServiceModeDisplay(
  serviceContent: DeviceServiceContent | null | undefined,
  code: Service | Service | null | undefined,
): ServiceModeDisplay | null {
  if (serviceContent == null || code == null) return null;

  let label: ServiceLabel | undefined;

  if (serviceContent === DeviceServiceContent.ElevatorServiceMessage) {
    label = SERVICE_LABELS[code as Service];
  } else if (serviceContent === DeviceServiceContent.EscalatorServiceMessage) {
    label = SERVICE_LABELS[code as Service];
  } else {
    return null;
  }

  const resolved = label ?? fallbackLabel(code);
  return {
    ...resolved,
    color: serviceColor(serviceContent, code),
  };
}
